import React from 'react';
import PropTypes from 'prop-types';
import { Check, X } from 'lucide-react';
import './LabelSelector.css';

/**
 * LabelSelector — Configurator
 *
 * Small selectable label button used in product configurators
 * (e.g., size selector: 1, 2, S, M, L, XL; or boolean yes/no toggles).
 *
 * Figma component set: 3812:1131
 * Variants: Status     = Default | Hover | Selected | Disabled
 *           Favourite  = true | false
 *           ShowValue  = true | false
 *           ShowBooleans = true | false  (Check / X icons)
 */

/* Inline favourite-heart SVG (16 × 16, Figma node custom/16/favourite-cls) */
const FavouriteIcon = () => (
  <svg
    className="cfg-label-selector__badge-svg"
    viewBox="0 0 15.6 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M14.228 2.224C13.574 1.51 12.836.8 11.154.8 9.47.8 8.638 1.333 7.8 2.316 6.961 1.423 6.128.8 4.446.8 2.763.8 1.932 1.51 1.372 2.224.81 2.937.696 4.1.883 5.26c.187 1.16 1.028 2.32 1.963 3.569.935 1.249 3.739 3.657 4.113 3.925.374.268.654.446.841.446s.467-.178.841-.446c.374-.268 3.178-2.676 4.113-3.925.935-1.249 1.776-2.41 1.963-3.569.187-1.16.072-2.327-.489-3.036Z"
      fill="#B50B0B"
      stroke="#fff"
      strokeWidth="1.6"
      strokeMiterlimit="10"
    />
  </svg>
);

const LabelSelector = ({
  children,
  selected = false,
  disabled = false,
  favourite = false,
  showValue = true,
  showBooleans = false,
  showYes = true,
  showNo = true,
  onClick,
  className = '',
  ...props
}) => {
  const Tag = disabled ? 'div' : 'button';

  const stateClass = disabled
    ? 'cfg-label-selector--disabled'
    : selected
      ? 'cfg-label-selector--selected'
      : '';

  return (
    <Tag
      className={`cfg-label-selector ${stateClass} ${className}`.trim()}
      onClick={!disabled ? onClick : undefined}
      disabled={Tag === 'button' ? disabled : undefined}
      type={Tag === 'button' ? 'button' : undefined}
      {...props}
    >
      {/* Favourite badge — outside the container, top-right overflow */}
      {favourite && !disabled && (
        <span className="cfg-label-selector__badge">
          <FavouriteIcon />
        </span>
      )}

      <span className="cfg-label-selector__container">
        {/* Value text (e.g. "1", "S", "M") */}
        {showValue && children != null && (
          <span className="cfg-label-selector__value">{children}</span>
        )}

        {/* Boolean icons (Check ✓ / X ✕) */}
        {showBooleans && (
          <span className="cfg-label-selector__booleans">
            {showYes && (
              <span className="cfg-label-selector__bool-icon">
                <Check size={20} />
              </span>
            )}
            {showNo && (
              <span className="cfg-label-selector__bool-icon">
                <X size={20} />
              </span>
            )}
          </span>
        )}
      </span>
    </Tag>
  );
};

LabelSelector.propTypes = {
  /** Label text displayed in the selector (e.g. "1", "S", "M") */
  children: PropTypes.node,
  /** Whether the selector is currently selected */
  selected: PropTypes.bool,
  /** Whether the selector is disabled */
  disabled: PropTypes.bool,
  /** Show the favourite heart badge (top-right corner) */
  favourite: PropTypes.bool,
  /** Show the value text */
  showValue: PropTypes.bool,
  /** Show boolean Check/X icons */
  showBooleans: PropTypes.bool,
  /** Show the Check (yes) icon inside booleans */
  showYes: PropTypes.bool,
  /** Show the X (no) icon inside booleans */
  showNo: PropTypes.bool,
  /** Click handler */
  onClick: PropTypes.func,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default LabelSelector;
